import { z } from "zod";
import { paginatedSchema, paginationQuerySchema } from "../common/pagination.js";
import { idSchema, isoDateTimeSchema, userSummarySchema } from "../common/primitives.js";
import { workspaceParamsSchema } from "../workspaces/workspace.schemas.js";

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/**
 * TASK_ASSIGNED: you were added to a task's assignees.
 * TASK_STATUS_CHANGED: a task you are assigned to (or created) moved to another status.
 * MESSAGE_MENTION: someone mentioned you in a chat message.
 * PROJECT_ADDED: you were added to a project.
 * TASK_COMMENT: someone commented on a task you watch.
 * TASK_MENTION: someone mentioned you in a task comment.
 */
export const NOTIFICATION_TYPES = [
  "TASK_ASSIGNED",
  "TASK_STATUS_CHANGED",
  "MESSAGE_MENTION",
  "PROJECT_ADDED",
  "TASK_COMMENT",
  "TASK_MENTION",
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];
export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES);

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const notificationSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  type: notificationTypeSchema,
  /** Who caused it; null once they left the workspace. */
  actor: userSummarySchema.nullable(),
  /** What it is about, as it was when the notification was created (task title, channel, project). */
  title: z.string(),
  /** Extra detail: the new status, or an excerpt of the message. */
  body: z.string().nullable(),
  project: z.object({ id: idSchema, key: z.string(), name: z.string() }).nullable(),
  /** Tasks have no page of their own yet: link to `project`. */
  task: z.object({ id: idSchema, key: z.string() }).nullable(),
  channelId: idSchema.nullable(),
  readAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});
export type Notification = z.infer<typeof notificationSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listNotificationsQuerySchema = paginationQuerySchema.extend({
  /** Only notifications that have not been read. */
  unread: z
    .enum(["true", "false"])
    .transform((value) => value === "true")
    .optional(),
});
export type ListNotificationsQuery = z.infer<typeof listNotificationsQuerySchema>;
export interface ListNotificationsQueryInput {
  limit?: number | undefined;
  cursor?: string | undefined;
  unread?: boolean | undefined;
}

export const notificationParamsSchema = workspaceParamsSchema.extend({ notificationId: idSchema });

export const setNotificationReadSchema = z.object({ read: z.boolean() });
export type SetNotificationReadInput = z.infer<typeof setNotificationReadSchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

/** Newest first; `nextCursor` continues towards older notifications. */
export const notificationsResponseSchema = paginatedSchema(notificationSchema);
export type NotificationsResponse = z.infer<typeof notificationsResponseSchema>;

export const notificationResponseSchema = z.object({ notification: notificationSchema });
export type NotificationResponse = z.infer<typeof notificationResponseSchema>;

export const unreadCountResponseSchema = z.object({ count: z.number().int().nonnegative() });
export type UnreadCountResponse = z.infer<typeof unreadCountResponseSchema>;
